'use client';

import React from 'react';
import { Box } from '@ui-library';
import CheckIn from '@/components/check-in';
import PrimaryButton from '@/components/theme/PrimaryButton';
import { FlexiPassDigitalKeys, RemotelockDigitalKeys } from '../digital-keys';
import { useIntl } from 'react-intl';
import {
  ReservationWithRateDetailsDTO,
  CheckInSettings,
  CheckOutSettings,
  Section,
  GuestBookPageSectionTypes,
} from '@/models';
import Link from 'next/link';
import { useKioskMode } from '@/hooks/useKioskMode';

interface ReservationPreviewProps {
  company: string;
  guestBookSlug: string;
  pageSlug: string;
  locale: string;
  settings: any;
  sections: Section[];
  preview: boolean;
  guestId: string | undefined;
  reservation?: ReservationWithRateDetailsDTO | null;
  reservations?: ReservationWithRateDetailsDTO[];
  checkInSettings: CheckInSettings | null;
  checkOutSettings: CheckOutSettings | null;
}

const ReservationPreview: React.FC<ReservationPreviewProps> = ({
  company,
  guestBookSlug,
  pageSlug,
  locale,
  settings,
  sections,
  preview,
  guestId,
  reservation,
  reservations,
  checkInSettings,
  checkOutSettings,
}) => {
  const intl = useIntl();
  const isGuestApp = pageSlug === '@guestapp';
  const activeReservation =
    reservation || (reservations && reservations[0]) || null;
  const checkInReservations =
    preview && activeReservation ? [activeReservation] : (reservations ?? []);

  const showCheckIn = !!(
    activeReservation &&
    checkInReservations.length &&
    guestId
  );

  const { isEnabled: isKioskMode } = useKioskMode();
  const showManageBooking = !guestId && !showCheckIn && !isKioskMode;

  const showRemoteLockKeys =
    activeReservation &&
    guestId &&
    sections.find(
      (s) => s.type === GuestBookPageSectionTypes.REMOTELOCK_DIGITAL_KEYS,
    )?.settings.visible === true;

  const showFlexiPassKeys =
    activeReservation &&
    guestId &&
    sections.find(
      (s) => s.type === GuestBookPageSectionTypes.FLEXIPASS_DIGITAL_KEYS,
    )?.settings.visible === true;

  if (!isGuestApp) {
    return null;
  }

  return (
    <>
      {showCheckIn && (
        <CheckIn
          key="check-in"
          locale={locale}
          company={company}
          guestBookSlug={guestBookSlug}
          settings={settings}
          reservations={checkInReservations}
          preview={preview}
          checkInSettings={checkInSettings}
          checkOutSettings={checkOutSettings}
          guestId={guestId}
        />
      )}

      {showFlexiPassKeys && (
        <FlexiPassDigitalKeys
          company={company}
          settings={settings}
          reservation={activeReservation}
          preview={preview}
          guestId={guestId}
        />
      )}

      {showRemoteLockKeys && (
        <RemotelockDigitalKeys
          company={company}
          guestId={guestId}
          settings={settings}
          reservation={activeReservation}
          preview={preview}
        />
      )}

      {showManageBooking && (
        <Link href={`/${locale}/${company}/login`}>
          <Box mb="5" mt="3">
            <PrimaryButton settings={settings} fullWidth>
              {intl.formatMessage({
                id: 'manage_my_booking',
                defaultMessage: 'Manage my booking',
              })}
            </PrimaryButton>
          </Box>
        </Link>
      )}
    </>
  );
};

export default ReservationPreview;
